import React, { useState, useEffect } from "react";
import { BrowserRouter as Router } from "react-router-dom";
import DesktopOnly from "./components/DesktopOnly";
import Navbar from "./components/Navbar";
import InitialDeposit from "./components/InitialDeposit";
import TradeForm from "./components/TradeForm";
import TradeHistory from "./components/TradeHistory";
import BacktestResults from "./pages/BacktestResults";
import { calculateProfit } from "./utils/tradeCalculations";
import { ThemeProvider, useTheme } from "./context/ThemeContext";

function AppContent() {
  const { darkMode } = useTheme();
  const [initialDeposit, setInitialDeposit] = useState(() => {
    const saved = localStorage.getItem("initialDeposit");
    return saved ? parseFloat(saved) : 0;
  });

  const [trades, setTrades] = useState(() => {
    const saved = localStorage.getItem("trades");
    return saved ? JSON.parse(saved) : [];
  });

  const [editingTradeIndex, setEditingTradeIndex] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);

  const [newTrade, setNewTrade] = useState({
    date:
      trades.length > 0
        ? trades[trades.length - 1].date
        : new Date().toISOString().split("T")[0],
    entryPrice: "",
    takeProfit: "",
    stopLoss: "",
    lotSize: "",
    entryType: "buy",
    outcome: "",
  });

  const [isBacktestComplete, setIsBacktestComplete] = useState(false);

  useEffect(() => {
    localStorage.setItem("trades", JSON.stringify(trades));
    localStorage.setItem("initialDeposit", initialDeposit.toString());
  }, [trades, initialDeposit]);

  const calculateCurrentBalance = () => {
    const totalProfit = trades.reduce(
      (acc, trade) => acc + calculateProfit(trade),
      0
    );
    return initialDeposit + totalProfit;
  };

  const handleEditTrade = (index) => {
    setEditingTradeIndex(index);
    setNewTrade(trades[index]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingTradeIndex !== null) {
      // Update existing trade
      const updatedTrades = [...trades];
      updatedTrades[editingTradeIndex] = newTrade;
      setTrades(updatedTrades);
      setEditingTradeIndex(null);
    } else {
      // Add new trade
      setTrades([...trades, newTrade]);
    }

    setNewTrade({
      date: newTrade.date,
      entryPrice: "",
      takeProfit: "",
      stopLoss: "",
      lotSize: "",
      entryType: "buy",
      outcome: "",
    });
  };

  const handleCancelEdit = () => {
    setEditingTradeIndex(null);
    setNewTrade({
      date:
        trades[trades.length - 1]?.date ||
        new Date().toISOString().split("T")[0],
      entryPrice: "",
      takeProfit: "",
      stopLoss: "",
      lotSize: "",
      entryType: "buy",
      outcome: "",
    });
  };

  const handleCompleteBacktest = () => {
    setIsBacktestComplete(true);
  };

  const handleReset = () => {
    setShowResetModal(true);
  };

  const confirmReset = () => {
    setTrades([]);
    setInitialDeposit(0);
    setIsBacktestComplete(false);
    setEditingTradeIndex(null);
    setNewTrade({
      date: new Date().toISOString().split("T")[0],
      entryPrice: "",
      takeProfit: "",
      stopLoss: "",
      lotSize: "",
      entryType: "buy",
      outcome: "",
    });
    setShowResetModal(false);
  };

  const handleDeleteTrade = (index) => {
    const updatedTrades = trades.filter((_, i) => i !== index);
    setTrades(updatedTrades);
  };

  return (
    <>
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div
            className={`${
              darkMode ? "bg-gray-800" : "bg-white"
            } rounded-lg shadow-xl max-w-md w-full p-6 relative z-[70]`}
          >
            <div className="text-center">
              <div
                className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${
                  darkMode ? "bg-red-900/20" : "bg-red-100"
                } mb-4`}
              >
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3
                className={`text-lg font-medium mb-2 ${
                  darkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Reset Backtest
              </h3>
              <p
                className={`text-sm ${
                  darkMode ? "text-gray-300" : "text-gray-500"
                } mb-6`}
              >
                Are you sure you want to reset the backtest? This will clear all
                trades and reset the initial deposit. This action cannot be
                undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetModal(false)}
                  className={`flex-1 px-4 py-2 rounded font-medium transition-colors duration-150 ${
                    darkMode
                      ? "bg-gray-700 hover:bg-gray-600 text-white"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReset}
                  className="flex-1 px-4 py-2 rounded font-medium bg-red-600 hover:bg-red-700 text-white transition-colors duration-150"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!initialDeposit ? (
        <div
          className={`min-h-screen flex items-center justify-center ${
            darkMode ? "bg-gray-900" : "bg-gray-100"
          }`}
        >
          <div className="w-full max-w-lg px-4">
            <InitialDeposit
              initialDeposit={initialDeposit}
              setInitialDeposit={setInitialDeposit}
            />
          </div>
        </div>
      ) : isBacktestComplete ? (
        <BacktestResults
          initialDeposit={initialDeposit}
          calculateCurrentBalance={calculateCurrentBalance}
          trades={trades}
          handleReset={handleReset}
        />
      ) : (
        <div
          className={`min-h-screen ${darkMode ? "bg-gray-900" : "bg-gray-100"}`}
        >
          <Navbar
            initialDeposit={initialDeposit}
            currentBalance={calculateCurrentBalance()}
            handleReset={handleReset}
            trades={trades}
            isBacktestComplete={isBacktestComplete}
            onCompleteBacktest={handleCompleteBacktest}
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
            <div className="mt-8 space-y-6">
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12 lg:col-span-4">
                  <div className="sticky top-24">
                    <TradeForm
                      newTrade={newTrade}
                      setNewTrade={setNewTrade}
                      handleSubmit={handleSubmit}
                      isEditing={editingTradeIndex !== null}
                      onCancelEdit={handleCancelEdit}
                    />
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-8">
                  <TradeHistory
                    trades={trades}
                    calculateProfit={calculateProfit}
                    onEditTrade={handleEditTrade}
                    onDeleteTrade={handleDeleteTrade}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <DesktopOnly>
          <AppContent />
        </DesktopOnly>
      </Router>
    </ThemeProvider>
  );
}

export default App;
